<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowLeft, ArrowRight, CalendarClock, CircleAlert, Send, Trash2 } from "lucide-vue-next";
import { useBriefStore } from "@/stores/briefs";
import { useProjectStore } from "@/stores/projects";
import { useSessionStore } from "@/stores/session";
import { v3 } from "@/services/v3";
import ProjectProgressBar from "@/modules/progress/ProjectProgressBar.vue";
import ProjectTimelineView from "@/modules/progress/ProjectTimelineView.vue";
import ClientProfilePanel from "@/modules/profile/ClientProfilePanel.vue";
import PartnerRequestChecklist from "@/components/PartnerRequestChecklist.vue";

const emit = defineEmits<{ openBrief: [id: number] }>();
const { t } = useI18n();
const briefStore = useBriefStore();
const projects = useProjectStore();
const session = useSessionStore();
const isPartner = computed(() => session.user?.account_type === "partner");
const filter = ref("all");
const workspacePane = ref<"projects" | "requests">("projects");
const timelineProjectId = ref<number | null>(null);
const deletingProjectId = ref<number | null>(null);
const partnerRequests = ref<any[]>([]);
const selectedRequestId = ref<number | null>(null);
const commentInput = ref("");
const requestBusy = ref(false);
const requestError = ref("");
let refreshTimer: number | undefined;

const selectedRequest = computed(() =>
  partnerRequests.value.find((item) => item.id === selectedRequestId.value),
);

const tabs = computed(() => [
  { id: "all", label: t("common.all"), count: projects.dashboard?.counts.all || 0 },
  { id: "draft", label: t("common.draft"), count: projects.dashboard?.counts.drafts || 0 },
  { id: "active", label: t("common.active"), count: projects.dashboard?.counts.active || 0 },
  {
    id: "acceptance",
    label: t("dashboard.awaitingAcceptance"),
    count: projects.dashboard?.counts.awaitingAcceptance || 0,
  },
  {
    id: "completed",
    label: t("common.completed"),
    count: projects.dashboard?.counts.completed || 0,
  },
]);
const visibleProjects = computed(() =>
  (projects.dashboard?.projects || []).filter((project) => {
    if (filter.value === "all") return true;
    if (filter.value === "draft") return project.status === "draft";
    if (filter.value === "active")
      return ["submitted", "in_progress"].includes(project.status);
    if (filter.value === "acceptance")
      return project.lifecycleState === "awaiting_acceptance";
    return project.status === "accepted";
  }),
);
const stageLabel = (stage: string) =>
  ({
    admin_review: t("dashboard.adminReview"), admin_client_review: t("dashboard.adminClientReview"),
    admin_confirmed: t("dashboard.adminConfirmed"), admin_revision: t("dashboard.adminRevision"),
    assigned: t("dashboard.assigned"), intake: t("dashboard.intake"), drafting: t("dashboard.drafting"),
    solution_drafting: t("dashboard.drafting"), solution_review: t("dashboard.solutionReview"),
    project_draft: t("dashboard.projectDraft"), submitted: t("dashboard.submitted"),
    team_accepted: t("dashboard.teamAccepted"), scheduled: t("dashboard.scheduled"),
    in_progress: t("dashboard.inProgress"), awaiting_acceptance: t("dashboard.awaiting"),
    revision: t("dashboard.revision"), accepted: t("common.completed"), completed: t("common.completed"),
  })[stage] || stage;

async function load() {
  const tasks: Promise<unknown>[] = [projects.loadDashboard(), briefStore.loadList()];
  if (isPartner.value) {
    tasks.push(
      v3.partnerRequests().then((result) => {
        partnerRequests.value = result.requests;
        if (selectedRequestId.value) {
          selectedRequestId.value =
            result.requests.find((item) => item.id === selectedRequestId.value)?.id || null;
        }
      }),
    );
  }
  await Promise.all(tasks);
}
async function showTimeline(briefId: number) {
  timelineProjectId.value = timelineProjectId.value === briefId ? null : briefId;
  if (timelineProjectId.value) await projects.loadTimeline(briefId);
}
async function deleteProject(briefId: number, title: string) {
  if (!window.confirm(t("dashboard.deleteConfirm", { title }))) return;
  deletingProjectId.value = briefId;
  try {
    await briefStore.deleteBrief(briefId);
    if (timelineProjectId.value === briefId) timelineProjectId.value = null;
    await projects.loadDashboard();
  } finally {
    deletingProjectId.value = null;
  }
}
function openRequest(id: number) {
  selectedRequestId.value = id;
  commentInput.value = "";
  requestError.value = "";
}
async function sendRequestComment() {
  if (!selectedRequest.value || commentInput.value.trim().length < 2) return;
  requestBusy.value = true;
  requestError.value = "";
  try {
    const result = await v3.commentPartnerRequest(
      selectedRequest.value.id,
      commentInput.value.trim(),
    );
    commentInput.value = "";
    await load();
    selectedRequestId.value = result.request.id;
  } catch (error) {
    requestError.value =
      error instanceof Error ? error.message : t("partnerRequests.commentFailed");
  } finally {
    requestBusy.value = false;
  }
}
const requestStatusLabel = (status: string) =>
  t(`partnerRequests.status.${status}`, status);
const requestActionLabel = (action: string) =>
  t(`partnerRequests.action.${action}`, action);
onMounted(() => {
  void load();
  refreshTimer = window.setInterval(() => void load(), 10000);
});
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <div class="dashboard">
    <header class="page-head">
      <div><h1>{{ t("dashboard.title") }}</h1><p>{{ t("dashboard.subtitle") }}</p></div>
      <button class="btn btn--primary" @click="emit('openBrief', 0)">{{ t("dashboard.newRequest") }}</button>
    </header>
    <nav v-if="isPartner" class="workspace-switch" :aria-label="t('partnerRequests.workspaceLabel')">
      <button :class="{ active: workspacePane === 'projects' }" @click="workspacePane = 'projects'; selectedRequestId = null">
        {{ t("partnerRequests.projects") }}<span>{{ projects.dashboard?.counts.all || 0 }}</span>
      </button>
      <button :class="{ active: workspacePane === 'requests' }" @click="workspacePane = 'requests'; selectedRequestId = null">
        {{ t("partnerRequests.requests") }}<span>{{ partnerRequests.length }}</span>
      </button>
    </nav>

    <template v-if="isPartner && workspacePane === 'requests'">
      <section v-if="!selectedRequest" class="partner-request-list">
        <header>
          <div><h2>{{ t("partnerRequests.title") }}</h2><p>{{ t("partnerRequests.subtitle") }}</p></div>
          <button class="btn btn--primary" @click="emit('openBrief', 0)">{{ t("dashboard.newRequest") }}</button>
        </header>
        <button v-for="item in partnerRequests" :key="item.id" class="partner-request-row" @click="openRequest(item.id)">
          <span class="request-main"><small>{{ item.categoryLabel }}</small><b>{{ item.title || t("partnerRequests.agentDrafting") }}</b><span>{{ item.summary || item.details?.desiredOutcome || item.details?.actualBehavior || t("partnerRequests.waitingDetails") }}</span></span>
          <span class="request-state"><b :class="item.status">{{ requestStatusLabel(item.status) }}</b><small>{{ item.completeness_score }}% · {{ item.updated_at.slice(0, 16).replace("T", " ") }}</small></span>
          <ArrowRight :size="18" />
        </button>
        <p v-if="!partnerRequests.length" class="empty">{{ t("partnerRequests.empty") }}</p>
      </section>

      <section v-else class="partner-request-detail">
        <button class="request-back" @click="selectedRequestId = null"><ArrowLeft :size="17" />{{ t("partnerRequests.back") }}</button>
        <header class="request-detail-head">
          <div><small>{{ t("partnerRequests.number", { id: selectedRequest.id }) }} · {{ selectedRequest.categoryLabel }}</small><h2>{{ selectedRequest.title }}</h2><p>{{ selectedRequest.summary }}</p></div>
          <span>{{ requestStatusLabel(selectedRequest.status) }}</span>
        </header>
        <div class="request-progress">
          <span class="done">{{ t("partnerRequests.created") }}</span><i />
          <span :class="{ done: ['in_progress','pending_admin_review','resolved'].includes(selectedRequest.status) }">{{ t("partnerRequests.assigned") }}</span><i />
          <span :class="{ done: ['pending_admin_review','resolved'].includes(selectedRequest.status) }">{{ t("partnerRequests.processing") }}</span><i />
          <span :class="{ done: selectedRequest.status === 'resolved' }">{{ t("partnerRequests.resolved") }}</span>
        </div>
        <PartnerRequestChecklist
          v-if="selectedRequest.details?.executionChecklist?.length"
          :items="selectedRequest.details?.executionChecklist"
        />
        <section v-if="selectedRequest.missingFields?.length" class="request-missing">
          <b>{{ t("partnerRequests.missing") }}</b>
          <span v-for="item in selectedRequest.missingFields" :key="item">{{ item }}</span>
        </section>
        <section class="request-timeline-section">
          <h3>{{ t("partnerRequests.timeline") }}</h3>
          <ol class="request-timeline">
            <li v-for="event in selectedRequest.events" :key="event.id">
              <i />
              <div><header><b>{{ requestActionLabel(event.action) }}</b><small>{{ event.actor_name }} · {{ event.created_at.slice(0, 16).replace("T", " ") }}</small></header><p v-if="event.note">{{ event.note }}</p></div>
            </li>
          </ol>
        </section>
        <section class="request-comment">
          <h3>{{ t("partnerRequests.comment") }}</h3>
          <p v-if="requestError" class="request-error">{{ requestError }}</p>
          <textarea v-model="commentInput" class="textarea" :placeholder="t('partnerRequests.commentPlaceholder')" />
          <button class="btn btn--primary" :disabled="requestBusy || commentInput.trim().length < 2" @click="sendRequestComment"><Send :size="16" />{{ t("partnerRequests.sendComment") }}</button>
        </section>
      </section>
    </template>

    <template v-else>
      <ClientProfilePanel />
      <nav class="segments" aria-label="项目筛选">
        <button v-for="tab in tabs" :key="tab.id" :class="{ active: filter === tab.id }" @click="filter = tab.id">
          {{ tab.label }}<span>{{ tab.count }}</span>
        </button>
      </nav>
      <section class="project-list">
        <article v-for="project in visibleProjects" :key="project.id" class="project-row">
          <button class="project-main" @click="emit('openBrief', project.id)">
            <span class="project-title"><b>{{ project.title }}</b><small>{{ stageLabel(project.lifecycleState) }}</small></span>
            <ProjectProgressBar :progress="project.progress" compact />
            <span class="project-date"><CalendarClock :size="16" />{{ project.progress.plannedEndAt || t("dashboard.noDate") }}</span>
            <span v-if="project.progress.blockedItems" class="project-alert"><CircleAlert :size="16" />{{ t("dashboard.blockedCount", { count: project.progress.blockedItems }) }}</span>
            <span v-else class="project-next">{{ project.progress.nextActor === "client" ? t("dashboard.waitYou") : t("dashboard.teamWorking") }}</span>
            <ArrowRight :size="18" />
          </button>
          <div class="project-row-actions"><button class="timeline-toggle" @click="showTimeline(project.id)">{{ timelineProjectId === project.id ? t("dashboard.collapseTimeline") : t("dashboard.showTimeline") }}</button><button class="delete-project" :disabled="deletingProjectId === project.id" :title="t('dashboard.deleteTitle', { title: project.title })" @click="deleteProject(project.id, project.title)"><Trash2 :size="16" /></button></div>
          <ProjectTimelineView v-if="timelineProjectId === project.id" :timeline="projects.timeline" />
        </article>
        <p v-if="!visibleProjects.length" class="empty">{{ t("dashboard.empty") }}</p>
      </section>
    </template>
  </div>
</template>

<style scoped>
.dashboard{min-height:calc(100vh - 64px);background:#f5f7f7}.page-head{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:26px 30px;background:#fff;border-bottom:1px solid var(--line)}.page-head h1{margin:0;font-size:24px}.page-head p{margin:4px 0 0;color:var(--muted)}.segments{display:flex;gap:0;padding:18px 30px 0;overflow:auto}.segments button{display:flex;align-items:center;gap:7px;min-height:38px;border:1px solid var(--line);border-right:0;padding:7px 12px;background:#fff;white-space:nowrap}.segments button:last-child{border-right:1px solid var(--line)}.segments button.active{background:var(--ink);color:#fff}.segments span{display:grid;min-width:20px;height:20px;place-items:center;background:#edf1f1;color:var(--muted);font-size:11px}.segments .active span{background:#fff;color:var(--ink)}.project-list{display:grid;gap:10px;padding:16px 30px 30px}.project-row{padding:0 16px;background:#fff;border:1px solid var(--line)}.project-main{display:grid;grid-template-columns:minmax(190px,1.3fr) minmax(180px,1fr) minmax(150px,.8fr) 120px 18px;align-items:center;gap:18px;width:100%;min-height:92px;border:0;background:transparent;text-align:left}.project-title b,.project-title small{display:block}.project-title b{font-size:16px}.project-title small{margin-top:5px;color:var(--brand-dark)}.project-date,.project-alert{display:flex;align-items:center;gap:7px;color:var(--muted);font-size:12px}.project-alert{color:var(--danger)}.project-next{color:var(--muted);font-size:12px}.timeline-toggle{margin:0 0 12px;border:0;background:transparent;color:var(--info);font-size:12px}.project-row :deep(.timeline){padding:15px 0;border-top:1px solid var(--line)}.empty{padding:50px;text-align:center;color:var(--muted);background:#fff;border:1px solid var(--line)}@media(max-width:1000px){.project-main{grid-template-columns:minmax(180px,1fr) minmax(160px,1fr) 18px}.project-date,.project-alert,.project-next{display:none}}@media(max-width:700px){.page-head{padding:18px 14px}.segments{padding:14px 14px 0}.project-list{padding:12px 14px 24px}.project-row{padding:0 12px}.project-main{grid-template-columns:minmax(0,1fr) 18px;gap:10px;padding:13px 0}.project-main :deep(.progress){grid-column:1/-1;grid-row:2}.project-title b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}}
.page-head .btn{flex:0 0 auto;white-space:nowrap}.segments{scrollbar-width:none}.segments::-webkit-scrollbar{display:none}
.project-row-actions{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px}.project-row-actions .timeline-toggle{margin:0}.delete-project{display:grid;width:38px;height:38px;place-items:center;border:1px solid var(--line);background:#fff;color:var(--danger)}.delete-project:disabled{opacity:.45}@media(max-width:700px){.project-row-actions{margin-bottom:8px}.delete-project{width:42px;height:42px}}
.workspace-switch{display:flex;gap:0;padding:18px 30px 0}.workspace-switch button{display:flex;align-items:center;gap:8px;min-height:42px;border:1px solid var(--line);border-right:0;padding:8px 18px;background:#fff;color:var(--muted)}.workspace-switch button:last-child{border-right:1px solid var(--line)}.workspace-switch button.active{background:var(--ink);color:#fff;font-weight:700}.workspace-switch span{display:grid;min-width:21px;height:21px;place-items:center;background:#edf1f1;color:var(--muted);font-size:11px}.workspace-switch .active span{background:#fff;color:var(--ink)}
.partner-request-list,.partner-request-detail{margin:16px 30px 30px;border:1px solid var(--line);background:#fff}.partner-request-list>header{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:18px 20px;border-bottom:1px solid var(--line)}.partner-request-list h2,.partner-request-list p{margin:0}.partner-request-list>header p{margin-top:4px;color:var(--muted)}.partner-request-row{display:grid;grid-template-columns:minmax(0,1fr) auto 18px;align-items:center;gap:18px;width:100%;min-height:88px;border:0;border-bottom:1px solid var(--line);padding:14px 20px;background:#fff;color:var(--ink);text-align:left}.partner-request-row:hover{background:#fbfcfc}.request-main{display:grid;min-width:0;gap:4px}.request-main>small{color:var(--brand-dark)}.request-main>b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:15px}.request-main>span{overflow:hidden;color:var(--muted);text-overflow:ellipsis;white-space:nowrap}.request-state{display:grid;justify-items:end;gap:5px}.request-state>b{color:#93611a;font-size:12px}.request-state>b.resolved{color:var(--success)}.request-state>small{color:var(--muted)}
.partner-request-detail{padding:20px}.request-back{display:flex;align-items:center;gap:5px;border:0;padding:0 0 14px;background:transparent;color:var(--brand-dark)}.request-detail-head{display:flex;justify-content:space-between;gap:16px;padding-bottom:16px;border-bottom:1px solid var(--line)}.request-detail-head h2{margin:4px 0}.request-detail-head p{margin:0;color:var(--muted)}.request-detail-head>span{color:#93611a;font-size:12px}.request-progress{display:grid;grid-template-columns:auto 1fr auto 1fr auto 1fr auto;align-items:center;gap:8px;padding:20px 0}.request-progress i{height:2px;background:#dfe5e6}.request-progress span{color:var(--muted);font-size:12px}.request-progress span.done{color:var(--success);font-weight:700}.partner-request-detail>section{padding:17px 0;border-top:1px solid var(--line)}.partner-request-detail h3{margin:0 0 10px}.request-missing{display:flex;flex-wrap:wrap;gap:6px}.request-missing>b{width:100%}.request-missing>span{padding:5px 8px;background:#fff4dc;color:#80591f;font-size:12px}.request-timeline{margin:0;padding:0;list-style:none}.request-timeline li{display:grid;grid-template-columns:14px minmax(0,1fr);gap:10px;position:relative;padding:0 0 18px}.request-timeline li>i{z-index:1;width:10px;height:10px;margin-top:5px;border:2px solid #fff;background:var(--brand);box-shadow:0 0 0 1px var(--brand)}.request-timeline li:not(:last-child)::before{content:"";position:absolute;left:4px;top:14px;bottom:0;width:1px;background:var(--line)}.request-timeline header{display:flex;justify-content:space-between;gap:10px}.request-timeline small{color:var(--muted)}.request-timeline p{margin:5px 0 0;white-space:pre-wrap;color:var(--muted)}.request-comment .textarea{min-height:96px;margin-bottom:8px}.request-comment .btn{display:flex;align-items:center;gap:6px}.request-error{padding:8px 10px;background:#fff2f0;color:var(--danger)}
@media(max-width:700px){.workspace-switch{padding:12px 14px 0}.workspace-switch button{flex:1;justify-content:center;padding:7px 8px}.partner-request-list,.partner-request-detail{margin:12px 14px 24px}.partner-request-list>header{align-items:flex-start;padding:14px}.partner-request-list>header p{display:none}.partner-request-row{grid-template-columns:minmax(0,1fr) 18px;gap:10px;padding:13px 14px}.request-state{grid-column:1/-1;grid-row:2;display:flex;justify-content:space-between}.partner-request-detail{padding:14px}.request-detail-head{display:grid}.request-progress{grid-template-columns:1fr 1fr;gap:7px}.request-progress i{display:none}.request-timeline header{display:grid;gap:2px}.request-comment .btn{width:100%;justify-content:center}}
</style>
